// Tiny hook + module-level fetch dedup for the code-review feature toggle.
//
// The flag lives in the DB and is fetched from /api/settings/code-review.
// All consumers (AppLayout nav, CodeReviewsPage, LinkedCodeReviewsBanner)
// call this hook; the underlying fetch is shared via a module-level
// promise so we don't pile up requests on initial paint.
//
// Refetch is exposed for the admin Settings toggle so flipping the flag
// updates the nav + banner without a page reload.

import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { DASHBOARD_PATHS } from '@/lib/paths';

interface Cache {
  enabled: boolean | null;   // null = unknown (initial / error)
  loading: Promise<void> | null;
  subs: Set<() => void>;
}

const cache: Cache = { enabled: null, loading: null, subs: new Set() };

async function loadOnce(): Promise<void> {
  if (cache.loading) return cache.loading;
  cache.loading = (async () => {
    try {
      const body = await api<{ enabled?: boolean }>(DASHBOARD_PATHS.settingsCodeReview);
      cache.enabled = !!body.enabled;
    } catch {
      cache.enabled = false;
    }
    for (const cb of cache.subs) { try { cb(); } catch { /* swallow */ } }
  })();
  return cache.loading;
}

/**
 * Manual refresh — call after a successful PUT on
 * /api/settings/code-review so the nav + banners pick up the new state
 * without waiting for a page reload.
 */
export function refreshCodeReviewEnabled(): void {
  cache.enabled = null;
  cache.loading = null;
  void loadOnce();
}

export function useCodeReviewEnabled(): { enabled: boolean | null; loading: boolean } {
  const [, setTick] = useState(0);

  useEffect(() => {
    const cb = () => setTick(t => t + 1);
    cache.subs.add(cb);
    if (cache.enabled === null && !cache.loading) void loadOnce();
    return () => { cache.subs.delete(cb); };
  }, []);

  return { enabled: cache.enabled, loading: cache.loading !== null && cache.enabled === null };
}
